import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useInfiniteScroll from './useInfiniteScroll';
import { searchDocsAPI, getAxiosCancelTokenSource, isCanceledRequestError } from '../common/services';
import { prepareDynamicDataFromBackendFacets, convertFilterToTags } from '../utils';
import { DOC_FILES_FIELD_ID, DOC_FILES_OPTION_IDS, getDocFilesSearchScope, getBackendDocSortValue } from '../constants/docs';

const DEFAULT_PAGE = { from: 0, size: 30 };

// The /docs/search filters (without paging and sort); null when no Files chip is selected.
const buildDocsSearchFilters = (filterVal, { houseId, houseObjectId, viewId } = {}) => {
    const { documentNo, displayName, [DOC_FILES_FIELD_ID]: selectedFiles, ...restFilters } = filterVal || {};
    const tags = convertFilterToTags(restFilters);
    const scope = getDocFilesSearchScope(selectedFiles);

    // Element scope: on only while the "Element docs" chip is selected,
    // and needs houseId + viewId (the backend requires both).
    const includeElementDocs = (selectedFiles || []).includes(DOC_FILES_OPTION_IDS.ELEMENT_DOCS);
    const elementScoped = Boolean(includeElementDocs && houseId && viewId && (houseObjectId || houseObjectId === 0));

    if (!elementScoped && !(selectedFiles || []).length) return null;

    const filters = {
        // element scope shows only that element's docs (house bucket
        // narrowed by houseObjectId); otherwise the chips decide
        scope: elementScoped ? ['house'] : (scope.length > 0 ? scope : undefined),
        houseId: houseId || undefined,
        houseObjectId: elementScoped ? String(houseObjectId) : undefined,
        viewId: elementScoped ? viewId : undefined,
        tags: tags.length > 0 ? tags : undefined,
    };
    if (documentNo) filters.documentNo = documentNo;
    if (displayName) filters.displayName = displayName;
    return filters;
};

/**
 * Docs search, filtering, and pagination — shared by the docs page, the manage
 * page and the project docs tab.
 *
 * @param {Object} options - Hook options
 * @param {Function} options.onDocsLoaded - Callback when docs are loaded
 * @param {string} options.houseId - Project scope for the search
 * @param {string} options.houseObjectId - Selected element id (requires houseId + viewId)
 * @param {string} options.viewId - The view the element id belongs to (a Forge dbId is only unique within one view)
 * @param {Object} options.defaultFilterValue - Initial filter value
 * @param {string} options.defaultSortValue - Initial sort value
 * @returns {Object} Docs state and handlers
 */
const useDocs = ({ onDocsLoaded, houseId, houseObjectId, viewId, defaultFilterValue, defaultSortValue = '-createdAt' } = {}) => {
    // Search results state
    const [docsList, setDocsList] = useState([]);
    const [facets, setFacets] = useState([]);
    const [totalDocs, setTotalDocs] = useState(0); // parents only, what the list pages by
    const [totalDocsWithAnnexes, setTotalDocsWithAnnexes] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [askEnabled, setAskEnabled] = useState(false);

    // Filter/sort state, seeded once from the defaults
    const defaultFilterRef = useRef(defaultFilterValue || {});
    const [filterValue, setFilterValue] = useState(defaultFilterRef.current);
    const defaultSortRef = useRef(defaultSortValue);
    const [sortValue, setSortValue] = useState(defaultSortRef.current);

    // Refs to track current values for scroll pagination (avoid stale closure issues in debounced handler)
    const filterValueRef = useRef(defaultFilterRef.current);
    const sortValueRef = useRef(defaultSortRef.current);
    const pageRef = useRef(DEFAULT_PAGE);
    const cancelTokenSourceRef = useRef(null);
    const searchGenerationRef = useRef(0);

    // Transform facets to MasterFilter format using existing utility function
    const { fields: filterFields, options: filterOptions } = useMemo(
        () => prepareDynamicDataFromBackendFacets(facets),
        [facets]
    );

    // Generate visible parameters from filter fields
    const visibleParams = useMemo(() => {
        return filterFields.reduce((acc, field) => ({ ...acc, [field.id]: true }), {});
    }, [filterFields]);

    // Fetch documents with pagination
    const fetchDocs = useCallback(async (from, size, filterVal, cancelToken) => {
        const filters = buildDocsSearchFilters(filterVal, { houseId, houseObjectId, viewId });

        // No Files chip selected = no context: empty list, chat disabled.
        // No request is made — the backend has no "nothing" scope.
        if (!filters) {
            return { hits: { results: [], total: 0 }, facets: [], askEnabled: false };
        }

        const params = { ...filters, from, size, sort: getBackendDocSortValue(sortValueRef.current) };
        const response = await searchDocsAPI(params, cancelToken);
        return response.data;
    }, [houseId, houseObjectId, viewId]);

    const searchFilters = useMemo(
        () => buildDocsSearchFilters(filterValue, { houseId, houseObjectId, viewId }),
        [filterValue, houseId, houseObjectId, viewId]
    );


    // Load the next page and append it (used by scroll and programmatic callers)
    const loadMore = useCallback(async () => {
        const currentPage = pageRef.current;
        const generation = searchGenerationRef.current;
        const newFrom = currentPage.from + currentPage.size;
        // Claim the offset up front so a concurrent caller can't request it again.
        pageRef.current = { from: newFrom, size: currentPage.size };

        try {
            const data = await fetchDocs(newFrom, currentPage.size, filterValueRef.current);
            if (searchGenerationRef.current !== generation) return; // a newer search owns the list
            const { hits, facets: responseFacets } = data;
            const newDocs = hits?.results || [];

            setDocsList(prev => [...prev, ...newDocs]);
            if (responseFacets) {
                setFacets(responseFacets);
            }
        } catch (error) {
            console.error('Error loading more docs:', error);
            if (searchGenerationRef.current === generation) {
                pageRef.current = currentPage; // failed page stays available for a retry
            }
        }
    }, [fetchDocs]);

    const hasMoreDocs = useCallback(
        () => totalDocs > (pageRef.current.from + pageRef.current.size),
        [totalDocs]
    );

    const {
        onScroll: handleScroll,
        isLoadingMore: isScrollLoader,
        reset: resetPaging,
    } = useInfiniteScroll({ loadMore, canLoadMore: hasMoreDocs });

    // Search documents (initial load or filter change)
    const searchDocs = useCallback(async (filterVal = {}) => {
        // Cancel any pending requests
        if (cancelTokenSourceRef.current) {
            cancelTokenSourceRef.current.cancel("Operation canceled due to new request.");
        }
        
        // Create new cancel token
        const cancelSource = getAxiosCancelTokenSource();
        cancelTokenSourceRef.current = cancelSource;
        const cancelToken = cancelSource.token;

        pageRef.current = DEFAULT_PAGE;
        searchGenerationRef.current += 1;
        resetPaging();
        setIsLoading(true);
        
        try {
            const data = await fetchDocs(DEFAULT_PAGE.from, DEFAULT_PAGE.size, filterVal, cancelToken);
            const { hits, facets: responseFacets, askEnabled } = data;
            
            const docs = hits?.results || [];
            setDocsList(docs);
            setFacets(responseFacets || []);
            setTotalDocs(hits?.total || 0);
            setTotalDocsWithAnnexes(hits?.totalWithAnnexes ?? hits?.total ?? 0);
            setAskEnabled(typeof askEnabled === 'boolean' ? askEnabled : true);
            
            // Callback for parent component
            onDocsLoaded && onDocsLoaded(docs);
        } catch (error) {
            if (isCanceledRequestError(error)) {
                console.log('Search request canceled:', error.message);
                return; // don't update state or loader on cancel
            }
            console.error('Error searching docs:', error);
            setDocsList([]);
            onDocsLoaded && onDocsLoaded([]);
        } finally {
            if (cancelTokenSourceRef.current === cancelSource) {
                setIsLoading(false);
                setIsInitialLoading(false);
            }
        }
    }, [fetchDocs, onDocsLoaded, resetPaging]);

    // Re-run the current search, so callers reloading after a change don't
    // have to track the filters themselves.
    const refreshDocs = useCallback(() => searchDocs(filterValueRef.current), [searchDocs]);

    // Handle filter change; an optional sort applies with the same search
    // (e.g. element selection switches the scope and the default sort at once)
    const onChangeFilter = useCallback((newFilterValue, newSortValue) => {
        if (newSortValue) {
            setSortValue(newSortValue);
            sortValueRef.current = newSortValue;
        }
        setFilterValue(newFilterValue);
        filterValueRef.current = newFilterValue;
        searchDocs(newFilterValue);
    }, [searchDocs]);

    // Handle sort change — re-fetch from the backend so order is correct across pages
    const onChangeSort = useCallback((newSortValue) => {
        setSortValue(newSortValue);
        sortValueRef.current = newSortValue;
        searchDocs(filterValueRef.current);
    }, [searchDocs]);

    // Initial load
    useEffect(() => {
        searchDocs(defaultFilterRef.current);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    return {
        // Docs data
        docsList,
        totalDocs,
        totalDocsWithAnnexes,
        isLoading,
        isInitialLoading,
        isScrollLoader,
        
        // Filter state and options for MasterFilter
        filterValue,
        searchFilters,
        filterOptions,
        filterFields,
        visibleParams,

        // Sort state
        sortValue,

        //ui flags
        askEnabled,

        // Handlers
        onChangeFilter,
        onChangeSort,
        handleScroll,
        loadMore,
        searchDocs,
        refreshDocs
    };
};

export default useDocs;
